/**
 * Stream operator settings.
 *
 * Centralised, typed accessors for every preference the Settings page
 * manages. Persisted in localStorage, keyed `stream.<section>.<key>`.
 *
 * Design rules:
 * - Components should consume settings via typed accessors or useSetting hook
 * - Writes fire a custom event for cross-component sync
 * - Theme preference delegates to the Zustand theme-store
 */

import {
  THEME_STORE_KEY,
  useThemeStore,
  type ThemePreference,
} from "@/shared/stores/theme-store";

// ---------------------------------------------------------------------------
// Key catalogue
// ---------------------------------------------------------------------------

/**
 * Every localStorage key Stream settings owns.
 */
export const SETTINGS_KEYS = {
  theme: THEME_STORE_KEY,
  notificationsMaster: "stream.notifications.master",
  notificationsVolume: "stream.notifications.volume",
  personalStatsVisible: "stream.personalStats.visible",
} as const;

export type SettingsKey = (typeof SETTINGS_KEYS)[keyof typeof SETTINGS_KEYS];

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_NOTIFICATION_MASTER = false;
export const DEFAULT_NOTIFICATION_VOLUME = 60;

// ---------------------------------------------------------------------------
// Same-tab change notification
// ---------------------------------------------------------------------------

/**
 * Dispatch a custom event that useSetting subscribes to. This bridges
 * the gap left by the native `storage` event, which only fires for writes
 * from *other* documents.
 */
export const SETTINGS_CHANGED_EVENT = "stream:settings-changed";

export interface SettingsChangedDetail {
  key: SettingsKey;
}

function emitChange(key: SettingsKey): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<SettingsChangedDetail>(SETTINGS_CHANGED_EVENT, {
      detail: { key },
    }),
  );
}

// ---------------------------------------------------------------------------
// Low-level storage helpers
// ---------------------------------------------------------------------------

function readRaw(key: SettingsKey): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: SettingsKey, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
    emitChange(key);
  } catch {
    // Silent no-op if storage unavailable
  }
}

function readBoolean(key: SettingsKey, fallback: boolean): boolean {
  const raw = readRaw(key);
  if (raw === null) return fallback;
  if (raw === "true") return true;
  if (raw === "false") return false;
  return fallback;
}

function writeBoolean(key: SettingsKey, value: boolean): void {
  writeRaw(key, value ? "true" : "false");
}

function readNumber(key: SettingsKey, fallback: number): number {
  const raw = readRaw(key);
  if (raw === null) return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function writeNumber(key: SettingsKey, value: number): void {
  writeRaw(key, String(value));
}

// ---------------------------------------------------------------------------
// Typed accessors
// ---------------------------------------------------------------------------

/**
 * Stream operator settings.
 *
 * Each accessor exposes `get()` and `set(value)`.
 */
export const settings = {
  theme: {
    /**
     * Theme preference. Delegates to the Zustand store so the
     * ThemeProvider subscription is authoritative.
     */
    get(): ThemePreference {
      return useThemeStore.getState().preference;
    },
    set(preference: ThemePreference): void {
      useThemeStore.getState().setPreference(preference);
      emitChange(SETTINGS_KEYS.theme);
    },
  },

  notifications: {
    master: {
      get(): boolean {
        return readBoolean(SETTINGS_KEYS.notificationsMaster, DEFAULT_NOTIFICATION_MASTER);
      },
      set(value: boolean): void {
        writeBoolean(SETTINGS_KEYS.notificationsMaster, value);
      },
    },
    volume: {
      get(): number {
        const value = readNumber(SETTINGS_KEYS.notificationsVolume, DEFAULT_NOTIFICATION_VOLUME);
        return clampVolume(value);
      },
      set(value: number): void {
        writeNumber(SETTINGS_KEYS.notificationsVolume, clampVolume(value));
      },
    },
  },

  personalStats: {
    visible: {
      get(): boolean {
        return readBoolean(SETTINGS_KEYS.personalStatsVisible, true);
      },
      set(value: boolean): void {
        writeBoolean(SETTINGS_KEYS.personalStatsVisible, value);
      },
    },
  },
} as const;

function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_NOTIFICATION_VOLUME;
  return Math.max(0, Math.min(100, Math.round(value)));
}
